import { useEffect, useMemo, useState } from "react";
import { useUserEventsAndMaterials } from "@/data/user.ts";
import { generateRedirectLink, INTERNAL_DATE_FORMAT } from "../agenda";
import dayjs, { Dayjs } from "dayjs";
import useAuth from "./useAuth";
import relativeTime from "dayjs/plugin/relativeTime.js";

dayjs.extend(relativeTime);

type Item = {
  ora: Dayjs;
  msg?: string;
  link: string;
  type: string;
  toNow?: string;
  createdAt?: string;
  lessonType?: string;
  notificationType?: number;
};

export default function useEventNotifications(): Item[] | undefined {
  const { getToken } = useAuth();
  const auth = getToken() as string;
  const { eventMaterials } = useUserEventsAndMaterials();

  const [events, setEvents] = useState<Array<Item> | undefined>([]);

  const eventsList = useMemo<Array<Item> | undefined>(
    () =>
      eventMaterials?.item?.events
        .filter((e) =>
          dayjs(e?.data, INTERNAL_DATE_FORMAT).isSame(dayjs(), "day"),
        )
        .map((e) => {
          if (
            e?.type?.toLowerCase() === "speaking" &&
            e?.titolo_argomento === "Mock test"
          ) {
            e.type = "MockTest";
          }

          return {
            ora: dayjs(e.data + " " + e.ora),
            link: generateRedirectLink(e.id, auth),
            type: e.type,
          };
        }),
    [eventMaterials, auth],
  );

  const updateState = () =>
    setEvents(
      eventsList
        ?.filter(
          (e) =>
            dayjs().isBefore(e.ora, "minute") &&
            e.ora.diff(dayjs(), "minute") < 31,
        )
        ?.map((e) => ({
          toNow: e.ora.toNow(),
          msg: `Your <i class='font-semibold not-italic'>${e.ora.format(
            "HH:mm",
          )}</i> class will start ${e.ora.toNow()}!`,
          notificationType: 0,
          lessonType: e.type,
          created_at: dayjs().format(INTERNAL_DATE_FORMAT),
          ...e,
        })),
    );

  useEffect(() => {
    const id = setInterval(
      () => {
        updateState();
      },
      1000 * 60 * 5,
    ); //fire every 5 minutes

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsList]);

  //populate events notifications onces
  useEffect(() => {
    updateState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsList]);

  return events;
}
